import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  Anchor,
  Badge,
  Box,
  Button,
  Group,
  Notification,
  Paper,
  Portal,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { IconArrowRight, IconLock } from "@tabler/icons-react";
import { ApiError } from "../api/client";
import { useDevelopmentGate } from "../api/developmentGate";
import { useLogout, useMe } from "../api/queries/auth";
import { displayNameOf } from "../api/types";
import { DownloadsPage } from "../downloads/DownloadsPage";
import { nativeRuntime } from "../platform/native";
import { observeNativeSession } from "../player/useNativeSession";
import { useLanguageSync } from "../i18n/useLanguage";
import { navigate, usePath } from "../router";
import { useRealtime } from "../realtime/useRealtime";
import { copyrightNotice } from "../siteMetadata";
import { AppSkeleton } from "./AppSkeleton";
import { ConfirmPage } from "./ConfirmPage";
import { ForgotDialog } from "./ForgotDialog";
import { LoginDialog } from "./LoginDialog";
import { MobileAuthorizePage } from "./MobileAuthorizePage";
import { ResetPage } from "./ResetPage";
import { SsoPage } from "./SsoPage";
import { FeedbackLine } from "./FeedbackLine";

export function DevelopmentGate({ children }: { children: ReactNode }) {
  const path = usePath();
  const runtime = nativeRuntime();
  useLanguageSync();
  useEffect(() => {
    if (!runtime || path !== "/") return;
    return observeNativeSession(
      runtime,
      (snapshot) => {
        if (
          snapshot.accountId !== null &&
          (snapshot.access === "offline-profile" || snapshot.access === "reauth-required")
        ) {
          navigate("/downloads", { replace: true });
        }
      },
      () => {},
    );
  }, [path, runtime]);
  if (path === "/downloads" && runtime) return <DownloadsPage runtime={runtime} />;
  return <OnlineDevelopmentGate path={path}>{children}</OnlineDevelopmentGate>;
}

/** The online content tree never mounts until the server has answered for this session. */
function OnlineDevelopmentGate({ children, path }: { children: ReactNode; path: string }) {
  const gate = useDevelopmentGate();
  const me = useMe();
  // Access changes must reach the gate while the gated application tree is still unmounted.
  useRealtime();

  // Emailed tokens and SSO callbacks must remain usable even while access is closed.
  // Render their existing pages directly, not App's content subscriptions/player hooks.
  if (path === "/reset") return <ResetPage />;
  if (path === "/confirm") return <ConfirmPage />;
  if (path === "/sso") return <SsoPage />;
  if (path === "/mobile/authorize") return <MobileAuthorizePage />;
  if (!me.isPending && !gate.isError && gate.data?.allowed) return children;
  return <GateScreen />;
}

function GateScreen() {
  const gate = useDevelopmentGate();
  const me = useMe();
  const logout = useLogout();
  const { t, i18n } = useTranslation();
  const [taps, setTaps] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [dialog, setDialog] = useState<"login" | "forgot" | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const unlocked = taps === 7;
  const failed = me.isError || gate.isError;
  const error = me.isError ? me.error : gate.error;
  const failureMessage =
    error instanceof ApiError
      ? error.status >= 500
        ? "developmentGate.serverError"
        : "developmentGate.requestError"
      : error instanceof TypeError
        ? "developmentGate.connectionError"
        : error instanceof SyntaxError
          ? "developmentGate.responseError"
          : "developmentGate.unknownError";

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    if (unlocked) panel.current?.focus();
  }, [unlocked]);

  function tapArchive() {
    if (unlocked) {
      panel.current?.focus();
      return;
    }
    const next = taps + 1;
    setTaps(next);
    if (next === 7) setToast(t("developmentGate.unlocked"));
    else if (next >= 3) setToast(t("developmentGate.steps", { count: 7 - next }));
  }

  if (!failed && (me.isPending || gate.isPending)) return <AppSkeleton />;

  return (
    <Box
      component="main"
      h="100dvh"
      style={{
        overflowY: "auto",
        position: "relative",
        isolation: "isolate",
        background:
          "radial-gradient(ellipse at 85% 15%, var(--mantine-color-accentDim-0), transparent 55%), var(--mantine-color-body)",
      }}
    >
      <GateGrooves />
      <Stack
        mih="100%"
        maw={1160}
        mx="auto"
        px={{ base: 24, sm: 48 }}
        pt="headerTop"
        pb={28}
        justify="space-between"
        gap={48}
      >
        <GateHeader failed={failed} unlocked={unlocked} onTap={tapArchive} />

        <Stack maw={560} gap={20} py={{ base: 16, sm: 52 }}>
          <Title
            order={1}
            fz={{ base: 28, sm: 32 }}
            lh={1.25}
            fw={600}
            style={{ letterSpacing: "-0.02em" }}
          >
            {t("developmentGate.title")}
          </Title>
          <Text size="md" c="dimmed" maw={510} lh={1.6}>
            {failed
              ? t(
                  me.isError
                    ? "developmentGate.sessionUnavailable"
                    : "developmentGate.availabilityUnavailable",
                )
              : t("developmentGate.description")}
          </Text>
          {failed && (
            <Accordion
              maw={510}
              chevronSize={16}
              chevronPosition="right"
              styles={{
                item: { border: 0 },
                control: {
                  padding: 0,
                  minHeight: 32,
                  width: "fit-content",
                  gap: 8,
                  background: "transparent",
                },
                label: { padding: 0, fontSize: 14, flex: "0 1 auto" },
                chevron: { marginInlineStart: 0 },
                content: { padding: "10px 0 0" },
              }}
            >
              <Accordion.Item value="failure">
                <Accordion.Control>{t("developmentGate.whatHappened")}</Accordion.Control>
                <Accordion.Panel>
                  <Text size="sm" c="dimmed" lh={1.6}>
                    {t(failureMessage, {
                      status: error instanceof ApiError ? error.status : undefined,
                    })}
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          )}
          {failed && (
            <Button
              size="md"
              w="fit-content"
              onClick={() => {
                void me.refetch();
                void gate.refetch();
              }}
            >
              {t("developmentGate.retry")}
            </Button>
          )}
          {!failed && (
            <Group gap={10} c="dimmed" align="flex-start" wrap="nowrap">
              <IconLock size={16} style={{ flexShrink: 0, marginTop: 4 }} aria-hidden="true" />
              <Text size="sm">
                {gate.data?.ends_at
                  ? t("developmentGate.until", {
                      when: new Date(gate.data.ends_at).toLocaleString(i18n.resolvedLanguage, {
                        dateStyle: "long",
                        timeStyle: "short",
                      }),
                    })
                  : t("developmentGate.indefinite")}
              </Text>
            </Group>
          )}
          {unlocked && (
            <Paper
              id="developer-access"
              ref={panel}
              tabIndex={-1}
              p={{ base: "md", sm: "xl" }}
              maw={510}
              style={{ outlineOffset: 4 }}
            >
              <Stack gap="sm">
                <Title order={2} fz="lg">
                  {t("developmentGate.accessTitle")}
                </Title>
                <Text size="sm" c="dimmed">
                  {t("developmentGate.accessDescription")}
                </Text>
                {me.data ? (
                  <>
                    <Text size="sm">
                      {t("developmentGate.denied", { name: displayNameOf(me.data) })}
                    </Text>
                    <Button size="md" onClick={() => logout.mutate()} loading={logout.isPending}>
                      {t("header.signOut")}
                    </Button>
                    <FeedbackLine role="alert">
                      {logout.isError && logout.error.message}
                    </FeedbackLine>
                  </>
                ) : (
                  <Button
                    size="md"
                    variant="filled"
                    disabled={me.isPending}
                    rightSection={<IconArrowRight size={17} aria-hidden="true" />}
                    onClick={() => setDialog("login")}
                  >
                    {t("header.logIn")}
                  </Button>
                )}
              </Stack>
            </Paper>
          )}
        </Stack>

        <GateFooter />
      </Stack>
      <GateToast message={toast} />
      {dialog === "login" && !me.data && (
        <LoginDialog onClose={() => setDialog(null)} onForgot={() => setDialog("forgot")} />
      )}
      {dialog === "forgot" && !me.data && (
        <ForgotDialog onClose={() => setDialog(null)} onLogin={() => setDialog("login")} />
      )}
    </Box>
  );
}

function GateGrooves() {
  return (
    <Box
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      <Box
        style={{
          position: "absolute",
          width: "min(85vw, 920px)",
          aspectRatio: "1",
          right: "-20%",
          top: "8%",
          borderRadius: "50%",
          opacity: 0.3,
          background:
            "repeating-radial-gradient(circle, transparent 0 27px, var(--mantine-color-default-border) 28px 29px, transparent 30px 54px)",
        }}
      />
    </Box>
  );
}

function GateHeader({
  failed,
  unlocked,
  onTap,
}: {
  failed: boolean;
  unlocked: boolean;
  onTap: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Group justify="space-between" gap="sm">
      <Group gap={12} wrap="nowrap">
        <img src="/logo.svg" alt="" width={36} height={36} />
        <Group gap={0} wrap="nowrap" fz={23} fw={650} style={{ letterSpacing: "-0.04em" }}>
          <span>jt</span>
          <UnstyledButton
            type="button"
            c="accent"
            fz="inherit"
            fw="inherit"
            mih={44}
            onClick={onTap}
            aria-expanded={unlocked}
            aria-controls={unlocked ? "developer-access" : undefined}
          >
            archive
          </UnstyledButton>
        </Group>
      </Group>
      {!failed && (
        <Badge color="accent" variant="light" size="lg">
          {t("developmentGate.badge")}
        </Badge>
      )}
    </Group>
  );
}

function GateFooter() {
  return (
    <Group
      justify="space-between"
      gap="sm"
      style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}
      pt={20}
    >
      <Text size="xs" c="dimmed">
        {copyrightNotice()}
      </Text>
      <Anchor href="https://jtarchive.net" size="xs" c="dimmed">
        jtarchive.net
      </Anchor>
    </Group>
  );
}

function GateToast({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <Portal>
      <Notification
        role="status"
        aria-live="polite"
        withCloseButton={false}
        color="accent"
        style={{
          position: "fixed",
          bottom: "max(24px, env(safe-area-inset-bottom))",
          left: "50%",
          transform: "translateX(-50%)",
          width: "max-content",
          maxWidth: "calc(100vw - 32px)",
          zIndex: 300,
        }}
      >
        {message}
      </Notification>
    </Portal>
  );
}
